import { useEffect, useMemo, useState } from "react";
import { previewTaxReceipt, sendTaxReceipt } from "../../api/adminInventory";

function emptyItem() {
  return { description: "", quantity: "1", unit_price: "" };
}

function dollarsToCents(value) {
  const parsed = parseFloat(value);
  if (Number.isNaN(parsed)) return 0;
  return Math.round(parsed * 100);
}

function centsToDollars(cents) {
  return (Number(cents || 0) / 100).toFixed(2);
}

export default function TaxReceipts() {
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [seller, setSeller] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await previewTaxReceipt();
        if (active && data.seller) setSeller(data.seller);
      } catch {
        /* seller details are optional for the form */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const totals = useMemo(() => {
    const totalCents = items.reduce((sum, item) => {
      const qty = Math.max(1, parseInt(item.quantity, 10) || 1);
      return sum + dollarsToCents(item.unit_price) * qty;
    }, 0);
    const gst = Math.floor(totalCents / 11);
    return {
      total: centsToDollars(totalCents),
      gst: centsToDollars(gst),
      exclusive: centsToDollars(totalCents - gst),
    };
  }, [items]);

  function updateItem(index, field, value) {
    setItems((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    );
  }

  function addItem() {
    setItems((current) => [...current, emptyItem()]);
  }

  function removeItem(index) {
    setItems((current) =>
      current.length === 1 ? [emptyItem()] : current.filter((_, i) => i !== index)
    );
  }

  async function loadOrder(e) {
    e?.preventDefault();
    const trimmed = orderId.trim();
    if (!trimmed) return;

    setLoading(true);
    setMessage(null);
    try {
      const receipt = await previewTaxReceipt(trimmed);
      setCustomerName(receipt.customer_name || "");
      setEmail(receipt.customer_email || email);
      setInvoiceNumber(receipt.invoice_number || "");
      setSeller(receipt.seller || seller);
      setItems(
        (receipt.items || []).map((item) => ({
          description: item.description || "",
          quantity: String(item.quantity || 1),
          unit_price: centsToDollars(item.unit_price_cents),
        }))
      );
      setMessage({
        type: "success",
        text: `Loaded order #${trimmed}${receipt.invoice_number ? ` (${receipt.invoice_number})` : ""}`,
      });
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
  }

  async function handleSend(e) {
    e.preventDefault();
    const recipient = email.trim();
    if (!recipient) {
      setMessage({ type: "error", text: "Enter the recipient email" });
      return;
    }

    const payloadItems = items
      .map((item) => ({
        description: item.description.trim(),
        quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
        unit_price_cents: dollarsToCents(item.unit_price),
      }))
      .filter((item) => item.description);

    if (!payloadItems.length && !orderId.trim()) {
      setMessage({ type: "error", text: "Add at least one line item, or load an order" });
      return;
    }

    setLoading(true);
    setMessage(null);
    try {
      const result = await sendTaxReceipt({
        email: recipient,
        customerName: customerName.trim(),
        transactionId: orderId.trim() || null,
        items: payloadItems,
      });
      setInvoiceNumber(result.invoice_number || invoiceNumber);
      setMessage({ type: "success", text: result.detail });
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="admin-tax-receipts">
      <form className="admin-scan-row" onSubmit={loadOrder}>
        <input
          type="text"
          inputMode="numeric"
          placeholder="Order ID (optional)"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          disabled={loading}
          autoComplete="off"
        />
        <button type="submit" disabled={loading || !orderId.trim()}>
          {loading ? "…" : "Load order"}
        </button>
      </form>
      <p className="admin-hint">
        Load a production order to fill the invoice, or enter line items manually. The tax invoice
        is emailed to the address below.
      </p>

      {message && <p className={`admin-message ${message.type}`}>{message.text}</p>}

      <section className="admin-section">
        <h3>Recipient</h3>
        <div className="admin-form-grid">
          <label className="full-width">
            Email
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="customer@email.com"
              autoComplete="off"
            />
          </label>
          <label className="full-width">
            Customer name
            <input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Optional"
            />
          </label>
        </div>
        {invoiceNumber ? (
          <p className="admin-hint">Invoice number: {invoiceNumber}</p>
        ) : null}
        {seller?.abn ? (
          <p className="admin-hint">
            Issued by {seller.company} · ABN {seller.abn}
          </p>
        ) : (
          <p className="admin-hint">
            Issued by {seller?.company || "The Average Collector Pty Ltd"}. Add TAX_INVOICE_ABN
            to backend .env to print your ABN on invoices.
          </p>
        )}
      </section>

      <section className="admin-section">
        <h3>Line items</h3>
        <ul className="admin-tax-items">
          {items.map((item, index) => (
            <li key={index}>
              <input
                className="admin-tax-desc"
                value={item.description}
                onChange={(e) => updateItem(index, "description", e.target.value)}
                placeholder="Description"
              />
              <input
                type="number"
                min={1}
                className="admin-tax-qty"
                value={item.quantity}
                onChange={(e) => updateItem(index, "quantity", e.target.value)}
                aria-label="Quantity"
              />
              <input
                type="number"
                min={0}
                step="0.01"
                className="admin-tax-price"
                value={item.unit_price}
                onChange={(e) => updateItem(index, "unit_price", e.target.value)}
                placeholder="0.00"
                aria-label="Unit price AUD"
              />
              <button
                type="button"
                aria-label="Remove line"
                onClick={() => removeItem(index)}
                disabled={loading}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className="admin-action-btn secondary" onClick={addItem}>
          Add line
        </button>
        <dl className="admin-tax-totals">
          <dt>Subtotal (ex GST)</dt>
          <dd>${totals.exclusive}</dd>
          <dt>GST (10%)</dt>
          <dd>${totals.gst}</dd>
          <dt>Total AUD</dt>
          <dd>${totals.total}</dd>
        </dl>
      </section>

      <button
        type="button"
        className="admin-action-btn"
        disabled={loading || !email.trim()}
        onClick={handleSend}
      >
        {loading ? "Sending…" : "Email tax invoice"}
      </button>
    </div>
  );
}
